/**
 * FlowComponents - Reusable building blocks for intentional flows
 *
 * These components create a consistent, beautiful UX across all flows:
 * - ChoiceCard: For binary/multiple choice selections
 * - ChoiceGrid: Grid layout for choice cards
 * - FlowInput: Styled input with label
 * - FlowTextarea: Styled textarea
 * - FlowSelect: Styled select dropdown
 * - FlowButton: Primary/secondary buttons
 * - FlowNavigation: Back/Continue buttons
 * - StepContainer: Wrapper with consistent spacing
 * - SummaryCard: For review steps
 * - QuickAction: Contextual action buttons
 */

import React, { forwardRef } from 'react';
import { motion } from 'framer-motion';

// ============================================================================
// CHOICE CARD - For intent selection
// ============================================================================

export const ChoiceCard = ({
  selected,
  onClick,
  icon,
  emoji,
  title,
  description,
  badge,
  disabled = false,
  size = 'default', // 'default' | 'compact' | 'large'
  className = '',
}) => {
  const sizeClasses = {
    compact: 'p-4',
    default: 'p-6',
    large: 'p-8',
  };

  return (
    <motion.button
      whileHover={!disabled ? { scale: 1.02 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
      onClick={() => !disabled && onClick?.()}
      disabled={disabled}
      className={`
        relative w-full text-left rounded-2xl border-2 transition-all duration-200
        ${sizeClasses[size]}
        ${selected
          ? 'border-brand bg-brand/5 dark:bg-brand/10'
          : 'border-gray-200 dark:border-[#2A2A2A] bg-white dark:bg-[#111111] hover:border-gray-300 dark:hover:border-[#3A3A3A]'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
    >
      {/* Selection indicator */}
      <div className={`
        absolute top-4 right-4 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all
        ${selected
          ? 'border-brand bg-brand'
          : 'border-gray-300 dark:border-[#3A3A3A]'
        }
      `}>
        {selected && (
          <motion.svg
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="w-3 h-3 text-black"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={3}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </motion.svg>
        )}
      </div>

      {/* Badge */}
      {badge && (
        <span className="absolute top-4 left-4 px-2 py-0.5 text-xs font-medium bg-brand/20 text-[#FF8C42] rounded-full">
          {badge}
        </span>
      )}

      {/* Icon or Emoji */}
      {(icon || emoji) && (
        <div className={`
          w-12 h-12 rounded-xl flex items-center justify-center mb-4
          ${selected ? 'bg-brand/20' : 'bg-gray-100 dark:bg-[#1A1A1A]'}
        `}>
          {emoji ? (
            <span className="text-2xl">{emoji}</span>
          ) : (
            <span className={selected ? 'text-brand' : 'text-gray-500 dark:text-[#666666]'}>
              {icon}
            </span>
          )}
        </div>
      )}

      {/* Content */}
      <h3 className={`
        font-semibold mb-1
        ${size === 'compact' ? 'text-sm' : 'text-base'}
        ${selected ? 'text-gray-900 dark:text-white' : 'text-gray-700 dark:text-[#999999]'}
      `}>
        {title}
      </h3>
      {description && (
        <p className={`
          text-gray-500 dark:text-[#666666]
          ${size === 'compact' ? 'text-xs' : 'text-sm'}
        `}>
          {description}
        </p>
      )}
    </motion.button>
  );
};

// ============================================================================
// CHOICE GRID - Layout for multiple choices
// ============================================================================

export const ChoiceGrid = ({
  children,
  columns = 2, // 1 | 2 | 3 | 4
  className = '',
}) => {
  const colClasses = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 sm:grid-cols-2',
    3: 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-2 lg:grid-cols-4',
  };

  return (
    <div className={`grid gap-4 ${colClasses[columns]} ${className}`}>
      {children}
    </div>
  );
};

// ============================================================================
// FLOW INPUT - Styled text input
// ============================================================================

export const FlowInput = forwardRef(({
  label,
  error,
  hint,
  prefix,
  suffix,
  size = 'default', // 'default' | 'large'
  className = '',
  ...props
}, ref) => {
  const sizeClasses = {
    default: 'px-4 py-3 text-sm',
    large: 'px-5 py-4 text-base',
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        {prefix && (
          <div className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
            <span className="text-gray-500 dark:text-[#666666]">{prefix}</span>
          </div>
        )}
        <input
          ref={ref}
          className={`
            w-full rounded-xl border bg-gray-50 dark:bg-[#0A0A0A]
            text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]
            focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50 focus:border-brand
            transition-all duration-200
            ${sizeClasses[size]}
            ${prefix ? 'pl-10' : ''}
            ${suffix ? 'pr-10' : ''}
            ${error
              ? 'border-red-500 dark:border-red-500'
              : 'border-gray-200 dark:border-[#2A2A2A]'
            }
          `}
          {...props}
        />
        {suffix && (
          <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
            <span className="text-gray-500 dark:text-[#666666]">{suffix}</span>
          </div>
        )}
      </div>
      {error && (
        <p className="mt-1.5 text-sm text-red-500">{error}</p>
      )}
      {hint && !error && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-[#666666]">{hint}</p>
      )}
    </div>
  );
});

FlowInput.displayName = 'FlowInput';

// ============================================================================
// FLOW TEXTAREA - Styled textarea
// ============================================================================

export const FlowTextarea = forwardRef(({
  label,
  error,
  hint,
  className = '',
  ...props
}, ref) => {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-2">
          {label}
        </label>
      )}
      <textarea
        ref={ref}
        className={`
          w-full px-4 py-3 rounded-xl border bg-gray-50 dark:bg-[#0A0A0A]
          text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-[#666666]
          focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50 focus:border-brand
          transition-all duration-200 resize-none
          ${error
            ? 'border-red-500 dark:border-red-500'
            : 'border-gray-200 dark:border-[#2A2A2A]'
          }
        `}
        {...props}
      />
      {error && (
        <p className="mt-1.5 text-sm text-red-500">{error}</p>
      )}
      {hint && !error && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-[#666666]">{hint}</p>
      )}
    </div>
  );
});

FlowTextarea.displayName = 'FlowTextarea';

// ============================================================================
// FLOW SELECT - Styled select dropdown
// ============================================================================

export const FlowSelect = forwardRef(({
  label,
  error,
  hint,
  options = [],
  placeholder,
  className = '',
  ...props
}, ref) => {
  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-[#999999] mb-2">
          {label}
        </label>
      )}
      <select
        ref={ref}
        className={`
          w-full px-4 py-3 rounded-xl border bg-gray-50 dark:bg-[#0A0A0A]
          text-gray-900 dark:text-white
          focus:outline-none focus:ring-2 focus:ring-[#FFB27D]/50 focus:border-brand
          transition-all duration-200 appearance-none
          bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22292.4%22%20height%3D%22292.4%22%3E%3Cpath%20fill%3D%22%23666666%22%20d%3D%22M287%2069.4a17.6%2017.6%200%200%200-13-5.4H18.4c-5%200-9.3%201.8-12.9%205.4A17.6%2017.6%200%200%200%200%2082.2c0%205%201.8%209.3%205.4%2012.9l128%20127.9c3.6%203.6%207.8%205.4%2012.8%205.4s9.2-1.8%2012.8-5.4L287%2095c3.5-3.5%205.4-7.8%205.4-12.8%200-5-1.9-9.2-5.5-12.8z%22%2F%3E%3C%2Fsvg%3E')]
          bg-[length:12px] bg-[right_16px_center] bg-no-repeat
          ${error
            ? 'border-red-500 dark:border-red-500'
            : 'border-gray-200 dark:border-[#2A2A2A]'
          }
        `}
        {...props}
      >
        {placeholder && (
          <option value="" disabled>
            {placeholder}
          </option>
        )}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {error && (
        <p className="mt-1.5 text-sm text-red-500">{error}</p>
      )}
      {hint && !error && (
        <p className="mt-1.5 text-sm text-gray-500 dark:text-[#666666]">{hint}</p>
      )}
    </div>
  );
});

FlowSelect.displayName = 'FlowSelect';

// ============================================================================
// FLOW BUTTON - Primary and secondary buttons
// ============================================================================

export const FlowButton = ({
  children,
  variant = 'primary', // 'primary' | 'secondary' | 'ghost' | 'danger'
  size = 'default', // 'default' | 'large' | 'small'
  loading = false,
  disabled = false,
  icon,
  iconPosition = 'left',
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-semibold rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-[#0A0A0A]';

  const variantClasses = {
    primary: 'bg-brand text-white hover:bg-brand-hover focus:ring-[#FFB27D]/50 disabled:bg-gray-300 dark:disabled:bg-[#2A2A2A]',
    secondary: 'bg-white dark:bg-[#111111] text-gray-700 dark:text-white border border-gray-200 dark:border-[#2A2A2A] hover:bg-gray-50 dark:hover:bg-[#1A1A1A] focus:ring-gray-200',
    ghost: 'text-gray-600 dark:text-[#999999] hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#1A1A1A] focus:ring-gray-200',
    danger: 'bg-red-500 text-white hover:bg-red-600 focus:ring-red-500/50',
  };

  const sizeClasses = {
    small: 'px-3 py-1.5 text-sm gap-1.5',
    default: 'px-5 py-2.5 text-sm gap-2',
    large: 'px-6 py-3.5 text-base gap-2.5',
  };

  return (
    <button
      disabled={disabled || loading}
      className={`
        ${baseClasses}
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${disabled || loading ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
      {...props}
    >
      {loading ? (
        <>
          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>Loading...</span>
        </>
      ) : (
        <>
          {icon && iconPosition === 'left' && icon}
          {children}
          {icon && iconPosition === 'right' && icon}
        </>
      )}
    </button>
  );
};

// ============================================================================
// FLOW NAVIGATION - Back and Continue buttons
// ============================================================================

export const FlowNavigation = ({
  onBack,
  onContinue,
  backLabel = 'Back',
  continueLabel = 'Continue',
  showBack = true,
  loading = false,
  disabled = false,
  className = '',
}) => {
  return (
    <div className={`flex items-center gap-3 pt-8 ${className}`}>
      {showBack && (
        <FlowButton
          variant="ghost"
          onClick={onBack}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          }
        >
          {backLabel}
        </FlowButton>
      )}
      <div className="flex-1" />
      <FlowButton
        variant="primary"
        size="large"
        onClick={onContinue}
        loading={loading}
        disabled={disabled}
        icon={
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        }
        iconPosition="right"
      >
        {continueLabel}
      </FlowButton>
    </div>
  );
};

// ============================================================================
// STEP CONTAINER - Consistent step wrapper
// ============================================================================

export const StepContainer = ({
  children,
  maxWidth = 'default', // 'narrow' | 'default' | 'wide' | 'full'
  className = '',
}) => {
  const maxWidthClasses = {
    narrow: 'max-w-md',
    default: 'max-w-lg',
    wide: 'max-w-2xl',
    full: 'max-w-none',
  };

  return (
    <div className={`mx-auto w-full ${maxWidthClasses[maxWidth]} ${className}`}>
      {children}
    </div>
  );
};

// ============================================================================
// SUMMARY CARD - For review steps
// ============================================================================

export const SummaryCard = ({
  title,
  items = [],
  onEdit,
  className = '',
  children,
}) => {
  return (
    <div className={`
      bg-gray-50 dark:bg-[#111111] rounded-2xl border border-gray-200 dark:border-[#2A2A2A] overflow-hidden
      ${className}
    `}>
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-[#2A2A2A]">
        <h3 className="font-medium text-gray-900 dark:text-white">{title}</h3>
        {onEdit && (
          <button
            onClick={onEdit}
            className="text-sm text-brand hover:text-[#FF8C42] font-medium"
          >
            Edit
          </button>
        )}
      </div>
      <div className="px-5 py-4 space-y-3">
        {/* Render children if provided, otherwise render items */}
        {children ? children : items.map((item, index) => (
          <div key={index} className="flex items-start justify-between gap-4">
            <span className="text-sm text-gray-500 dark:text-[#666666]">{item.label}</span>
            <span className="text-sm font-medium text-gray-900 dark:text-white text-right">
              {item.value}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// AMOUNT DISPLAY - Large formatted amount
// ============================================================================

export const AmountDisplay = ({
  amount,
  currency = 'USD',
  label,
  size = 'default', // 'default' | 'large'
  variant = 'default', // 'default' | 'highlight'
  className = '',
}) => {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return (
    <div className={`text-center ${className}`}>
      {label && (
        <p className="text-sm text-gray-500 dark:text-[#666666] mb-1">{label}</p>
      )}
      <p className={`
        font-bold
        ${size === 'large' ? 'text-4xl' : 'text-2xl'}
        ${variant === 'highlight' ? 'text-brand' : 'text-gray-900 dark:text-white'}
      `}>
        {formatter.format(amount)}
      </p>
    </div>
  );
};

// ============================================================================
// DIVIDER - Visual separator
// ============================================================================

export const FlowDivider = ({
  label,
  className = '',
}) => {
  if (label) {
    return (
      <div className={`flex items-center gap-4 ${className}`}>
        <div className="flex-1 h-px bg-gray-200 dark:bg-[#2A2A2A]" />
        <span className="text-xs font-medium text-gray-400 dark:text-[#666666] uppercase tracking-wider">
          {label}
        </span>
        <div className="flex-1 h-px bg-gray-200 dark:bg-[#2A2A2A]" />
      </div>
    );
  }

  return (
    <div className={`h-px bg-gray-200 dark:bg-[#2A2A2A] ${className}`} />
  );
};

// ============================================================================
// INFO BOX - Contextual information
// ============================================================================

export const InfoBox = ({
  children,
  variant = 'info', // 'info' | 'warning' | 'success' | 'tip'
  icon,
  className = '',
}) => {
  const variantStyles = {
    info: 'bg-blue-50 dark:bg-blue-500/10 border-blue-200 dark:border-blue-500/20 text-blue-800 dark:text-blue-300',
    warning: 'bg-yellow-50 dark:bg-yellow-500/10 border-yellow-200 dark:border-yellow-500/20 text-yellow-800 dark:text-yellow-300',
    success: 'bg-green-50 dark:bg-green-500/10 border-green-200 dark:border-green-500/20 text-green-800 dark:text-green-300',
    tip: 'bg-brand/10 border-brand/20 text-[#FF8C42]',
  };

  const defaultIcons = {
    info: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    ),
    success: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    tip: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
      </svg>
    ),
  };

  return (
    <div className={`
      flex items-start gap-3 p-4 rounded-xl border
      ${variantStyles[variant]}
      ${className}
    `}>
      <span className="flex-shrink-0 mt-0.5">
        {icon || defaultIcons[variant]}
      </span>
      <div className="text-sm">
        {children}
      </div>
    </div>
  );
};

// ============================================================================
// QUICK ACTIONS - Row of action buttons
// ============================================================================

export const QuickActions = ({
  actions = [],
  className = '',
}) => {
  return (
    <div className={`flex flex-wrap gap-2 ${className}`}>
      {actions.map((action, index) => (
        <button
          key={index}
          onClick={action.onClick}
          disabled={action.disabled}
          className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium
            transition-all duration-200
            ${action.variant === 'primary'
              ? 'bg-brand text-white hover:bg-brand-hover'
              : 'bg-gray-100 dark:bg-[#1A1A1A] text-gray-700 dark:text-[#999999] hover:bg-gray-200 dark:hover:bg-[#2A2A2A]'
            }
            ${action.disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          {action.icon}
          {action.label}
        </button>
      ))}
    </div>
  );
};

export default {
  ChoiceCard,
  ChoiceGrid,
  FlowInput,
  FlowTextarea,
  FlowSelect,
  FlowButton,
  FlowNavigation,
  StepContainer,
  SummaryCard,
  AmountDisplay,
  FlowDivider,
  InfoBox,
  QuickActions,
};
